import { createAsyncThunk } from "@reduxjs/toolkit";
import { environment } from "../../environments";
import {
  GetTaskRequest,
  GetTaskResponse,
  GetTaskDetailsRequest,
  GetTaskDetailsResponse,
  CreateTaskRequest,
  CreateTaskResponse,
  DeleteTaskRequest,
  DeleteTaskResponse,
  SetTaskStatusRequest,
  SetTaskStatusResponse,
  SnoozeTaskRequest,
  SnoozeTaskResponse,
} from "../../models/Task.model";

const API_URL = environment.apiUrl;

export const getTasks = createAsyncThunk<
  GetTaskResponse,
  GetTaskRequest,
  { rejectValue: string }
>("tasks/getTasks", async (requestData, { rejectWithValue }) => {
  try {
    const response = await fetch(`${API_URL}Task/GetTasks`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requestData),
    });

    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}`;
      try {
        const errorData = await response.text();
        try {
          const errorJson = JSON.parse(errorData);
          errorMessage = errorJson.message || errorJson.Message || errorMessage;
        } catch {
          errorMessage = errorData || errorMessage;
        }
      } catch (readError) {}
      throw new Error(errorMessage);
    }
    const data: GetTaskResponse = await response.json();
    if (!data.success) return rejectWithValue(data.message || "Failed to fetch tasks");
    return data;
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : "Unknown error occurred");
  }
});

export const getTaskDetails = createAsyncThunk<
  GetTaskDetailsResponse,
  GetTaskDetailsRequest,
  { rejectValue: string }
>("tasks/getTaskDetails", async (requestData, { rejectWithValue }) => {
  try {
    const response = await fetch(`${API_URL}Task/GetTaskDetails`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requestData),
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data: GetTaskDetailsResponse = await response.json();
    if (!data.success) return rejectWithValue(data.message || "Failed to fetch task details");
    return data;
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : "Unknown error occurred");
  }
});

export const createTask = createAsyncThunk<
  CreateTaskResponse,
  CreateTaskRequest,
  { rejectValue: string }
>("tasks/createTask", async (requestData, { rejectWithValue }) => {
  try {
    const localDateString = `${requestData.dueDate.getFullYear()}-${(requestData.dueDate.getMonth() + 1).toString().padStart(2, "0")}-${requestData.dueDate.getDate().toString().padStart(2, "0")}T${requestData.dueDate.getHours().toString().padStart(2, "0")}:${requestData.dueDate.getMinutes().toString().padStart(2, "0")}:${requestData.dueDate.getSeconds().toString().padStart(2, "0")}`;
    const requestWithLocalDate = { ...requestData, dueDate: localDateString };
    const response = await fetch(`${API_URL}Task/CreateTask`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requestWithLocalDate),
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data: CreateTaskResponse = await response.json();
    if (!data.success) return rejectWithValue(data.message || "Failed to create task");
    return data;
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : "Unknown error occurred");
  }
});

export const deleteTask = createAsyncThunk<
  DeleteTaskResponse,
  DeleteTaskRequest,
  { rejectValue: string }
>("tasks/deleteTask", async (requestData, { rejectWithValue }) => {
  try {
    const response = await fetch(`${API_URL}Task/DeleteTask`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requestData),
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data: DeleteTaskResponse = await response.json();
    if (!data.success) return rejectWithValue(data.message || "Failed to delete task");
    return data;
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : "Unknown error occurred");
  }
});

export const setTaskStatus = createAsyncThunk<
  SetTaskStatusResponse,
  SetTaskStatusRequest,
  { rejectValue: string }
>("tasks/setTaskStatus", async (requestData, { rejectWithValue }) => {
  try {
    const response = await fetch(`${API_URL}Task/SetTaskStatus`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requestData),
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data: SetTaskStatusResponse = await response.json();
    if (!data.success) return rejectWithValue(data.message || "Failed to update task status");
    return data;
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : "Unknown error occurred");
  }
});

export const snoozeTask = createAsyncThunk<
  SnoozeTaskResponse,
  SnoozeTaskRequest,
  { rejectValue: string }
>("tasks/snoozeTask", async (requestData, { rejectWithValue }) => {
  try {
    const response = await fetch(`${API_URL}Task/SnoozeTask`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requestData),
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data: SnoozeTaskResponse = await response.json();
    if (!data.success) return rejectWithValue(data.message || "Failed to snooze task");
    return data;
  } catch (error) {
    return rejectWithValue(error instanceof Error ? error.message : "Unknown error occurred");
  }
});
